import { isValidEpochMs } from './isValidEpochMs'
import type { LinkTrustRevocationsState } from '~/interfaces/LinkTrustRevocationsState'

type RevocationStorage = Pick<Storage, 'getItem'>

const failClosed = (): LinkTrustRevocationsState => ({
  revocationsByIdentity: {},
  universalRevokedAt: Date.now()
})

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const DISALLOWED_IDENTITY_KEYS = new Set(['__proto__', 'constructor', 'prototype'])

// JSON.parse assigns __proto__/constructor/prototype as real own keys, never through the prototype accessor.
const isDisallowedIdentityKey = (identityId: string): boolean =>
  DISALLOWED_IDENTITY_KEYS.has(identityId)

const parseRevocationsByIdentity = (value: unknown): Record<string, number> | undefined => {
  if (!isPlainObject(value)) return undefined
  const entries: Record<string, number> = {}
  for (const [identityId, revokedAt] of Object.entries(value)) {
    if (isDisallowedIdentityKey(identityId)) return undefined
    if (!isValidEpochMs(revokedAt)) return undefined
    entries[identityId] = revokedAt
  }
  return entries
}

const parseUniversalRevokedAt = (value: unknown): number | null | undefined => {
  if (value === null) return null
  return isValidEpochMs(value) ? value : undefined
}

// storage is a thunk so passing rawValue never forces evaluating an unavailable global.
export const readLinkTrustRevocationsFromStorage = (
  storage: () => RevocationStorage,
  key: string,
  rawValue?: string | null
): LinkTrustRevocationsState => {
  try {
    const stored = rawValue === undefined ? storage().getItem(key) : rawValue
    if (stored === null) return { revocationsByIdentity: {}, universalRevokedAt: null }
    const parsed: unknown = JSON.parse(stored)
    if (!isPlainObject(parsed)) return failClosed()

    const rawRevocationsByIdentity = parsed['revocationsByIdentity']
    const revocationsByIdentity =
      rawRevocationsByIdentity === undefined
        ? {}
        : parseRevocationsByIdentity(rawRevocationsByIdentity)
    if (revocationsByIdentity === undefined) return failClosed()

    const universalRevokedAt = parseUniversalRevokedAt(parsed['universalRevokedAt'] ?? null)
    if (universalRevokedAt === undefined) return failClosed()

    return { revocationsByIdentity, universalRevokedAt }
  } catch {
    return failClosed()
  }
}
